/**
 * Monitoring Service
 * Tracks suspicious activity and provides alerts for potential bot attacks
 */

import { appConfig } from "../config/appConfig";

interface SuspiciousActivity {
  type:
    | "rate_limit_exceeded"
    | "spam_detected"
    | "captcha_failed"
    | "unusual_pattern";
  severity: "low" | "medium" | "high" | "critical";
  sessionId: string;
  userAgent?: string;
  ipAddress?: string;
  timestamp: Date;
  details: string;
  metadata?: Record<string, any>;
}

interface ActivityStats {
  totalRequests: number;
  blockedRequests: number;
  spamDetected: number;
  captchaFailures: number;
  uniqueSessions: number;
  suspiciousSessions: string[];
}

class MonitoringService {
  private activities: SuspiciousActivity[] = [];
  private sessionStats: Map<string, { count: number; lastActivity: Date }> =
    new Map();
  private readonly MAX_ACTIVITIES = 1000; // Keep last 1000 activities
  private readonly SUSPICIOUS_THRESHOLD =
    appConfig.security.monitoring.suspiciousThreshold;

  /**
   * Record suspicious activity
   */
  recordActivity(activity: Omit<SuspiciousActivity, "timestamp">): void {
    const fullActivity: SuspiciousActivity = {
      ...activity,
      timestamp: new Date(),
    };

    this.activities.push(fullActivity);

    // Keep only recent activities
    if (this.activities.length > this.MAX_ACTIVITIES) {
      this.activities = this.activities.slice(-this.MAX_ACTIVITIES);
    }

    // Update session stats
    this.updateSessionStats(activity.sessionId);

    // Log critical activities
    if (activity.severity === "critical") {
      console.error("🚨 CRITICAL SECURITY ALERT:", fullActivity);
      this.sendAlert(fullActivity);
    }

    // Check for suspicious patterns
    this.checkSuspiciousPatterns(activity.sessionId);
  }

  /**
   * Update session statistics
   */
  private updateSessionStats(sessionId: string): void {
    const now = new Date();
    const existing = this.sessionStats.get(sessionId);

    if (existing) {
      existing.count++;
      existing.lastActivity = now;
    } else {
      this.sessionStats.set(sessionId, {
        count: 1,
        lastActivity: now,
      });
    }
  }

  /**
   * Check for suspicious patterns in session activity
   */
  private checkSuspiciousPatterns(sessionId: string): void {
    const sessionData = this.sessionStats.get(sessionId);
    if (!sessionData) return;

    // Check if session has too many requests
    if (sessionData.count > this.SUSPICIOUS_THRESHOLD) {
      this.recordActivity({
        type: "unusual_pattern",
        severity: "high",
        sessionId,
        details: `Session exceeded ${this.SUSPICIOUS_THRESHOLD} requests (${sessionData.count} total)`,
        metadata: { requestCount: sessionData.count },
      });
    }

    // Check for rapid-fire requests (more than 5 in 10 seconds)
    const recentActivities = this.activities.filter(
      (activity) =>
        activity.sessionId === sessionId &&
        Date.now() - activity.timestamp.getTime() < 10000
    );

    if (recentActivities.length > 5) {
      this.recordActivity({
        type: "unusual_pattern",
        severity: "medium",
        sessionId,
        details: `Rapid-fire requests detected: ${recentActivities.length} in 10 seconds`,
        metadata: { rapidFireCount: recentActivities.length },
      });
    }
  }

  /**
   * Get activity statistics
   */
  getActivityStats(): ActivityStats {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    const recentActivities = this.activities.filter(
      (activity) => activity.timestamp >= oneHourAgo
    );

    const uniqueSessions = new Set(recentActivities.map((a) => a.sessionId));
    const suspiciousSessions = Array.from(uniqueSessions).filter(
      (sessionId) => {
        const sessionData = this.sessionStats.get(sessionId);
        return sessionData && sessionData.count > this.SUSPICIOUS_THRESHOLD;
      }
    );

    return {
      totalRequests: recentActivities.length,
      blockedRequests: recentActivities.filter(
        (a) => a.type === "rate_limit_exceeded"
      ).length,
      spamDetected: recentActivities.filter((a) => a.type === "spam_detected")
        .length,
      captchaFailures: recentActivities.filter(
        (a) => a.type === "captcha_failed"
      ).length,
      uniqueSessions: uniqueSessions.size,
      suspiciousSessions,
    };
  }

  /**
   * Get recent suspicious activities
   */
  getRecentActivities(limit: number = 50): SuspiciousActivity[] {
    return this.activities
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit);
  }

  /**
   * Check if a session is suspicious
   */
  isSessionSuspicious(sessionId: string): boolean {
    const sessionData = this.sessionStats.get(sessionId);
    if (!sessionData) return false;

    return sessionData.count > this.SUSPICIOUS_THRESHOLD;
  }

  /**
   * Get session statistics
   */
  getSessionStats(
    sessionId: string
  ): { count: number; lastActivity: Date } | null {
    return this.sessionStats.get(sessionId) || null;
  }

  /**
   * Clear old data to prevent memory leaks
   */
  cleanup(): void {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // Remove old activities
    this.activities = this.activities.filter(
      (activity) => activity.timestamp >= oneDayAgo
    );

    // Remove old session stats
    const sessionsToDelete: string[] = [];
    this.sessionStats.forEach((data, sessionId) => {
      if (data.lastActivity < oneDayAgo) {
        sessionsToDelete.push(sessionId);
      }
    });

    sessionsToDelete.forEach((sessionId) => {
      this.sessionStats.delete(sessionId);
    });
  }

  /**
   * Send alert for critical activities
   */
  private sendAlert(activity: SuspiciousActivity): void {
    // In a real implementation, this would send alerts to:
    // - Admin dashboard
    // - Email notifications
    // - Slack/Discord webhooks
    // - Security monitoring systems

    console.error("🚨 SECURITY ALERT:", {
      type: activity.type,
      severity: activity.severity,
      sessionId: activity.sessionId,
      details: activity.details,
      timestamp: activity.timestamp,
    });

    // You could implement actual alerting here:
    // - Send to admin dashboard via WebSocket
    // - Send email to administrators
    // - Log to external monitoring service
  }

  /**
   * Export data for analysis
   */
  exportData(): {
    activities: SuspiciousActivity[];
    sessionStats: Array<{
      sessionId: string;
      count: number;
      lastActivity: Date;
    }>;
    stats: ActivityStats;
  } {
    return {
      activities: this.activities,
      sessionStats: (() => {
        const stats: Array<{
          sessionId: string;
          count: number;
          lastActivity: Date;
        }> = [];
        this.sessionStats.forEach((data, sessionId) => {
          stats.push({
            sessionId,
            ...data,
          });
        });
        return stats;
      })(),
      stats: this.getActivityStats(),
    };
  }
}

// Create singleton instance
export const monitoringService = new MonitoringService();

// Cleanup old data every hour
setInterval(() => {
  monitoringService.cleanup();
}, 60 * 60 * 1000);

export default MonitoringService;
